import React, { useState, useEffect } from 'react';

interface OfflineIndicatorProps {
  onOnline?: () => void;
  onOffline?: () => void;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ 
  onOnline, 
  onOffline 
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setWasOffline(true);
      if (onOnline) onOnline();
      
      // Show success message briefly
      setTimeout(() => {
        setWasOffline(false);
      }, 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      if (onOffline) onOffline();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Update state based on navigator.onLine (more reliable than external fetch)
    // The browser's online/offline events are sufficient for most use cases
    const updateOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    // Check connection status periodically using navigator.onLine
    // This avoids CSP violations and is more reliable
    const interval = setInterval(() => {
      updateOnlineStatus();
    }, 30000); // Check every 30 seconds

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [onOnline, onOffline]);

  if (isOnline && !wasOffline) {
    return null; // Don't show anything when online
  }

  if (wasOffline && isOnline) {
    // Show reconnection success message
    return (
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
        <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
          <span className="text-xl">✅</span>
          <span className="text-sm font-semibold">Connection restored! You're back online.</span>
        </div>
      </div>
    );
  }

  // Show offline banner
  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white px-4 py-3 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xl">📡</span>
          <div>
            <p className="font-semibold text-sm">You're offline</p>
            <p className="text-xs opacity-90">Some features may be unavailable. Your data will sync when connection is restored.</p>
          </div>
        </div>
        <div className="animate-pulse">
          <div className="h-2 w-2 bg-white rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

export default OfflineIndicator;

